// Logo tiểu đội (QĐ 0485): ảnh tiểu đội tự tải, logo kho có sẵn, hay ô tròn chữ cái đầu — kèm khung
// đội đang đeo. ≥ 72px vẽ tranh khung đủ (vòng kim loại + ruy băng in chữ); nhỏ hơn chỉ vòng kim loại
// gọn để không tràn sang chữ bên cạnh (bản mẫu 1a, như khung cá nhân bản gọn).

import { useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { avatarSrcFor } from "@/lib/staff-avatar";
import {
  HOLE_R,
  SQUAD_FRAME_GEOMETRY,
  frameArt,
  frameDef,
  presetArt,
  ribbonText,
  squadInitials,
  type FrameCode,
} from "./catalog";
import type { SquadBadge } from "./api";
import "./tieu-doi.css";

type LogoSource = Pick<SquadBadge, "name"> &
  Partial<Pick<SquadBadge, "hrmName" | "logo" | "preset" | "frame">>;

export type FrameShown = { code: FrameCode; month?: string | undefined; rank?: number | undefined };

type Props = {
  squad: LogoSource;
  size: number;
  /** Khung thay cho khung tiểu đội đang đeo (bục, tủ khung, xem thử); null = không khung. */
  frame?: FrameShown | null | undefined;
  /** Chữ ruy băng của tranh đủ; mặc định theo khung (HẠNG 2, T11/2026…). */
  ribbon?: string | undefined;
  /** auto: ≥ 72px tranh đủ, nhỏ hơn vòng gọn. */
  mode?: "auto" | "full" | "ring";
  className?: string | undefined;
};

export function SquadLogo({ squad, size, frame, ribbon, mode = "auto", className }: Props) {
  const src = squad.logo ? avatarSrcFor(squad.logo, size) : presetArt(squad.preset);
  const [failed, setFailed] = useState<string | null>(null);
  const showImage = !!src && failed !== src;

  const shown: FrameShown | null =
    frame !== undefined
      ? frame
      : squad.frame && frameDef(squad.frame.code)
        ? { code: squad.frame.code as FrameCode, month: squad.frame.month, rank: squad.frame.rank }
        : null;
  const def = shown ? frameDef(shown.code) : undefined;
  const full = !!def && (mode === "full" || (mode === "auto" && size >= 72));

  const label = def ? `Logo ${squad.name}, khung ${def.label}` : `Logo ${squad.name}`;
  const circle = (
    <span
      className={cn("td-lg", def && !full && `td-vg td-vg-${def.metal}`, !full && className)}
      style={{ width: size, height: size }}
      role={full ? undefined : "img"}
      aria-label={full ? undefined : label}
    >
      {showImage ? (
        <img
          className="td-lg-anh"
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(src)}
        />
      ) : (
        <span className="td-lg-chu" style={{ fontSize: Math.max(8, Math.round(size * 0.34)) }}>
          {squadInitials(squad.name)}
        </span>
      )}
    </span>
  );
  if (!full || !def || !shown) return circle;

  // Tranh 512px: lỗ giữa bán kính HOLE_R% ⇒ cạnh tranh = cỡ logo ÷ đường kính lỗ, nới 4% cho logo
  // chui dưới mép kim loại (công thức bản mẫu đã duyệt).
  const geo = SQUAD_FRAME_GEOMETRY[shown.code];
  const F = (size / ((2 * HOLE_R) / 100)) * 1.04;
  const off = (F - size) / 2;
  const pct = (v: number) => (F * v) / 100;
  const text = ribbon ?? ribbonText(shown.code, shown.month, shown.rank);
  const rh = pct(geo.ribbon.h);
  const style: CSSProperties = {
    width: F,
    height: F,
    margin: `${-pct(geo.top)}px ${-pct(100 - geo.right)}px ${-pct(100 - geo.bottom)}px ${-pct(geo.left)}px`,
  };
  return (
    <span className={cn("td-kd", className)} style={style} role="img" aria-label={label}>
      <span className="td-kd-logo" style={{ left: off, top: off }}>
        {circle}
      </span>
      <img className="td-kd-art" src={frameArt(shown.code)} alt="" decoding="async" />
      {text && (
        <span
          className="td-kd-chu"
          aria-hidden="true"
          style={{
            left: pct(geo.ribbon.x),
            top: pct(geo.ribbon.y),
            width: pct(geo.ribbon.w),
            height: rh,
            fontSize: Math.max(7, rh * 0.6),
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
